/**
 * Some keys have underscores because values come from API
 *
 * POIs:
 * - From Schools POIs
 * -- high_schools & primary_schools
 *
 * - From Enhanced POIs
 * -- alcohol, clothing, common_needs, conference_centers, entertainment, hospitals, libraries, pharmacies, retail, shops_and_services
 */

const namespace = {
  attribution: "Propulsé par",
  time: {
    minute_one: "Minute",
    minute_other: "Minutes",
    hour_one: "Heure",
    hour_other: "Heures",
  },
  distance: {
    meter: "m",
    kilometer: "km",
    yard: "vg",
    mile: "mil",
  },
  language: {
    english: "Anglais",
    french: "Français",
    spanish: "Espagnol",
  },
  map: {
    marker: {
      schoolAttributes: {
        levels: "Niveau",
        grades: "Notes",
        languages: "Langues",
        types: "Type",
        board: "Commission",
        programs: "Programmes",
      },
      schoolCta: "Lien vers l'école",
    },
  },
  scores: {
    cafes: {
      title: "Cafés",
      description:
        "Une mesure de la quantité et de la diversité des cafés à proximité.",
    },
    car_friendly: {
      title: "Voiture",
      description:
        "Représente la fluidité de la circulation et la facilité de stationnement.",
    },
    cycling_friendly: {
      title: "Vélo",
      description:
        "Représente à quel point la zone est adaptée au cyclisme en termes d'infrastructures cyclables à proximité et de rues calmes, ainsi que le nombre de destinations pouvant être atteintes à vélo.",
    },
    daycares: {
      title: "Garderies",
      description: "Représente la distance jusqu'à la garderie la plus proche.",
    },
    greenery: {
      title: "Verdure",
      description:
        "Quantité d'espaces verts à proximité et couverture de la canopée des arbres.",
    },
    groceries: {
      title: "Épiceries",
      description: "Une mesure de la quantité d'épiceries à proximité.",
    },
    high_schools: {
      title: "Écoles secondaires",
      description:
        "Représente la distance jusqu'à l'école secondaire la plus proche.",
    },
    historic: {
      title: "Historique",
      description:
        "Une mesure de la quantité et de la taille des bâtiments patrimoniaux, et de l'âge moyen des bâtiments dans la région.",
    },
    nightlife: {
      title: "Vie nocturne",
      description:
        "Représente la quantité et la diversité des bars à proximité.",
    },
    parks: {
      title: "Parcs",
      description:
        "Une mesure de la quantité et de la taille des parcs à proximité.",
    },
    pedestrian_friendly: {
      title: "Marche",
      description:
        "Une mesure de la proximité des services de base et du niveau de convivialité d'y aller à pied.",
    },
    primary_schools: {
      title: "Écoles primaires",
      description:
        "Une mesure de la distance jusqu'à l'école primaire la plus proche.",
    },
    quiet: {
      title: "Silencieux",
      description: "Une mesure des niveaux moyens de bruit à proximité.",
    },
    restaurants: {
      title: "Restaurants",
      description:
        "Représente la quantité et la diversité des restaurants à proximité.",
    },
    shopping: {
      title: "Magasins",
      description:
        "Une mesure de la quantité et de la diversité de magasins de mode et de grands magasins à proximité.",
    },
    transit_friendly: {
      title: "Transport public",
      description:
        "Une mesure de la fréquence et du niveau de service de transport en commun.",
    },
    vibrant: {
      title: "Dynamisme",
      description: "Représente le niveau d'activité de la rue à proximité.",
    },
    wellness: {
      title: "Bien-être",
      description:
        "Représente à quel point un emplacement favorise le bien-être des individus.",
    },
  },
  pois: {
    groceries: "Générales",
    groceries_specialized: "Spécialisées",
    shopping: "Magasinage",
    nightlife: "Vie nocturne",
    restaurants: "Restaurants",
    cafes: "Cafés",
    daycares: "Garderies",
    bus_stops: "Autobus",
    rapid_transit_stations: "Métro",
    train_stations: "Train",
    parks: "Parcs",
    healthy_restaurants: "Restaurants sains",
    fitness: "Entraînement physique",
    sport_facilities: "Installations sportives",
    health_care: "Soins de santé",
    pet_care: "Soins pour animaux",
    car_fueling_stations: "Stations-service",
    art_culture_science: "Art, culture, science",
    home_improvement: "Magasin de rénovation",
    personal_care: "Soins personnels",
    high_schools: "Écoles secondaires",
    primary_schools: "Écoles primaires",
    alcohol: "Alcool",
    clothing: "Vêtements",
    common_needs: "Besoins quotidiens",
    conference_centers: "Salles de conférence",
    entertainment: "Divertissement",
    hospitals: "Hôpitaux",
    libraries: "Librairies",
    pharmacies: "Pharmacies",
    retail: "Magasins de détail",
    shops_and_services: "Magasins et services",
  },
  valueDrivers: {
    bus_service_access: "Services d'autobus",
    shops_and_services_access: "Magasins et services",
    coffee_shop_access: "Cafés",
    clothing_and_accessory_store_access: "Magasins de vêtements",
    common_needs_access: "Nécessités communes",
    department_store_access: "Grands magasins",
    entertainment_venue_access: "Lieux de divertissement",
    fitness_establishment_access: "Conditionnement physique",
    nightlife_access: "Vie nocturne",
    pedestrian_oriented_street_access: "Rues accessibles aux piétons",
    rapid_transit_service_access: "Service de transport en commun rapide",
    restaurant_access: "Restaurants",
    specialty_grocery_store_access: "Épiceries spécialisées",
    sports_facility_access: "Installations sportives",
    train_service_access: "Gares",
    nearest_highway_proximity: "Accès à l'autoroute",
    nearest_pharmacy_proximity: "Pharmacies à proximité",
    nearest_bicycle_share_station_proximity:
      "Stations de vélos en libre-service à proximité",
    bicycle_path_access: "Stations de vélopartage à proximité",
    bicycle_share_station_access: "Partage de vélo",
    nearest_park_area: "Grand parc à proximité",
    bicycle_infrastructure_quality: "Infrastructure pour vélos",
    high_schools: "Écoles secondaires",
    primary_schools: "Écoles primaires",
    quiet: "Calme",
    daycares: "Garderies",
    parks: "Parcs",
  },
} as const;

export default namespace;
